import React, { useState, useEffect } from 'react';
import { X, Upload } from 'lucide-react';
import { convertFileToBase64 } from '../../utils/imageUtils';
import toast from 'react-hot-toast';

// 공통 아이템 인터페이스
interface BaseItem {
  id: string;
  created_at: string;
  updated_at: string;
  title: string;
  description: string;
  reference_image: string;
  notification_type: string;
}

// 공통 생성 요청 인터페이스
interface BaseCreateRequest {
  title: string;
  description: string;
  reference_image?: string;
}

interface NoticeModalProps<T extends BaseItem, K extends BaseCreateRequest> {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: K) => Promise<boolean>;
  editingNotice?: T | null;
  isLoading?: boolean;
  modalTitle?: string;
  createButtonText?: string;
  editButtonText?: string;
}

const NoticeModal = <T extends BaseItem, K extends BaseCreateRequest>({
  isOpen,
  onClose,
  onSubmit,
  editingNotice = null,
  isLoading = false,
  modalTitle = '항목',
  createButtonText = '생성',
  editButtonText = '수정',
}: NoticeModalProps<T, K>) => {
  const [formData, setFormData] = useState<K>({
    title: '',
    description: '',
    reference_image: '',
  } as K);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 편집 모드일 때 폼 데이터 초기화
  useEffect(() => {
    if (editingNotice) {
      setFormData({
        title: editingNotice.title,
        description: editingNotice.description,
        reference_image: editingNotice.reference_image || '',
      } as K);
      setImagePreview(editingNotice.reference_image || '');
    } else {
      setFormData({
        title: '',
        description: '',
        reference_image: '',
      } as K);
      setImagePreview('');
    }
    setErrors({});
  }, [editingNotice, isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // 에러 클리어
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const base64Data = await convertFileToBase64(file, {
        includeDataPrefix: false,
        maxSize: 5 * 1024 * 1024
      });
      
      const previewUrl = await convertFileToBase64(file, {
        includeDataPrefix: true,
        maxSize: 5 * 1024 * 1024
      });
      
      setFormData(prev => ({
        ...prev,
        reference_image: base64Data,
      }));
      setImagePreview(previewUrl);
      
      if (errors.image) {
        setErrors(prev => ({
          ...prev,
          image: '',
        }));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '파일 업로드 중 오류가 발생했습니다.';
      setErrors(prev => ({
        ...prev,
        image: errorMessage,
      }));
      toast.error(errorMessage);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      reference_image: '',
    }));
    setImagePreview('');
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = '제목을 입력해주세요.';
    } else if (formData.title.length > 100) {
      newErrors.title = '제목은 100자 이내로 입력해주세요.';
    }

    if (!formData.description.trim()) {
      newErrors.description = '내용을 입력해주세요.';
    } else if (formData.description.length > 2000) {
      newErrors.description = '내용은 2000자 이내로 입력해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    const success = await onSubmit(formData);
    if (success) {
      onClose();
    }
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={handleBackgroundClick}
    >
      <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[100vh] overflow-hidden">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-slate-800">
          <h3 className="text-lg font-semibold text-white">
            {editingNotice ? `${modalTitle} ${editButtonText}` : `${modalTitle} ${createButtonText}`}
          </h3>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] custom-scrollbar">
          <div className="space-y-6">
            {/* 제목 입력 */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-slate-300 mb-2">
                제목 <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                disabled={isLoading}
                className={`w-full px-4 py-3 bg-slate-800 border rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  errors.title ? 'border-red-500' : 'border-slate-700'
                }`}
                placeholder={`${modalTitle} 제목을 입력하세요`}
                maxLength={100}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-400">{errors.title}</p>
              )}
              <p className="mt-2 text-xs text-slate-500">
                {formData.title.length}/100자
              </p>
            </div>

            {/* 내용 입력 */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-slate-300 mb-2">
                내용 <span className="text-red-400">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                disabled={isLoading}
                rows={8}
                className={`w-full px-4 py-3 bg-slate-800 border rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none disabled:opacity-50 disabled:cursor-not-allowed ${
                  errors.description ? 'border-red-500' : 'border-slate-700'
                }`}
                placeholder={`${modalTitle} 내용을 입력하세요`}
                maxLength={2000}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-400">{errors.description}</p>
              )}
              <p className="mt-2 text-xs text-slate-500">
                {formData.description.length}/2000자
              </p>
            </div>

            {/* 이미지 업로드 */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                첨부 이미지 (선택사항)
              </label>
              
              {!imagePreview ? (
                <div className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center hover:border-slate-600 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={isLoading}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <Upload className="w-8 h-8 text-slate-500" />
                    <p className="text-slate-400">
                      클릭하여 이미지를 업로드하세요
                    </p>
                    <p className="text-xs text-slate-500">
                      JPG, PNG, GIF (최대 5MB)
                    </p>
                  </label>
                </div>
              ) : (
                <div className="relative border border-slate-700 rounded-lg p-4">
                  <img
                    src={imagePreview}
                    alt="미리보기"
                    className="w-full max-h-48 object-contain rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={removeImage}
                    disabled={isLoading}
                    className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {errors.image && (
                <p className="mt-1 text-sm text-red-400">{errors.image}</p>
              )}
            </div>
          </div>
        </form>

        {/* 버튼 */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-800">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors border border-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-4 py-2 text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors border border-transparent disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading && (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            )}
            {editingNotice ? editButtonText : createButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoticeModal; 